import { Cell, FormControl, Grid } from 'bold-ui'
import { useAlert } from 'components/alert'
import { Form, FormPrompt, SubmitButton, TextField } from 'components/form'
import DateRangeField, { LocalDateRange } from 'components/form/field/DateRangeField'
import { FormApi } from 'final-form'
import createDecorator from 'final-form-calculate'
import { useSalvarFechamentoMutation } from 'graphql/hooks.generated'
import moment from 'moment'
import React from 'react'
import { FormRenderProps } from 'react-final-form'
import { metaPath } from 'util/metaPath'
import { createValidator, maxLength, required } from 'util/validation'

import convert from './convert'
import { MotivoFechamentoSelectField } from './MotivoFechamentoSelectField'

const validate = createValidator<FechamentoFormModel>(
  {
    periodo: [required],
    motivo: [required],
    especifique: [maxLength(300)],
  },
  (values, errors) => {
    if (!errors.periodo) errors.periodo = required(values.periodo.startDate)
    if (!errors.periodo) errors.periodo = required(values.periodo.endDate)
    if (!errors.periodo && moment(values.periodo.startDate).isBefore(moment().toDate(), 'date')) {
      errors.periodo = 'Data inicial é menor que a data atual.'
    }
    if (!errors.periodo && moment(values.periodo.endDate).isBefore(values.periodo.startDate, 'date')) {
      errors.periodo = 'Data inicial é maior que a data final.'
    }

    if (values.motivo === 'Outro') {
      errors.especifique = required(values.especifique)
    }
    return errors
  }
)

export interface FechamentoFormModel {
  periodo: LocalDateRange
  motivo: string
  especifique: string
}

interface FechamentoFormProps {
  idLotacao: ID

  onUpdated(): void
}

const name = metaPath<FechamentoFormModel>()

const motivoDecorator = createDecorator({
  field: name.motivo.absolutePath(),
  updates: {
    especifique: (motivoValue: string, allValues: FechamentoFormModel) => {
      return motivoValue !== 'Outro' ? undefined : allValues.especifique
    },
  },
})

export default function FechamentoForm({ idLotacao, onUpdated }: FechamentoFormProps) {
  const [mutationSalvarFechamento] = useSalvarFechamentoMutation()
  const alert = useAlert()

  const handleSubmit = (values: FechamentoFormModel, form: FormApi) => {
    return mutationSalvarFechamento({ variables: { input: convert(values, idLotacao) } }).then(() => {
      alert('success', 'Fechamento foi salvo com sucesso.')
      form.getRegisteredFields().forEach((field) => form.resetFieldState(field))
      setTimeout(form.reset)
      onUpdated()
    })
  }

  const renderForm = (formProps: FormRenderProps<FechamentoFormModel>) => {
    const outroSelected = formProps.values.motivo === 'Outro'

    return (
      <form onSubmit={formProps.handleSubmit} noValidate>
        <FormPrompt />
        <Grid wrap gap={1}>
          <Cell size={4}>
            <DateRangeField name={name.periodo} label='Período' required minDate={moment().toDate()} />
          </Cell>

          <Cell size={3}>
            <MotivoFechamentoSelectField name={name.motivo} label='Motivo' required />
          </Cell>

          <Cell size={3}>
            <TextField
              name={name.especifique}
              label='Especifique'
              required={outroSelected}
              disabled={!outroSelected}
              maxLength={300}
            />
          </Cell>

          <Cell size={2}>
            <FormControl label={<span>&nbsp;</span>}>
              <SubmitButton handleSubmit={formProps.handleSubmit} size='small'>
                Adicionar
              </SubmitButton>
            </FormControl>
          </Cell>
        </Grid>
      </form>
    )
  }

  return (
    <Form<FechamentoFormModel>
      onSubmit={handleSubmit}
      validate={validate}
      render={renderForm}
      decorators={[motivoDecorator]}
    />
  )
}
